/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { CategoryTabIds } from '@shared/constants';

import { CrossCampaignEntryLargeScreen } from '../cross-campaign-entry-large-screen';
import { useCrossCampaignEntry } from '.';

export const useCrossCampaignEntryLargeScreen = (): {
    showMenu: (tabId: CategoryTabIds) => void;
    hideMenu: (delay?: number) => void;
    isMenuShown: (tabId: CategoryTabIds) => boolean;
    isExpandableTab: (tabId: CategoryTabIds) => boolean;
    CrossCampaignEntryLargeScreen: () => React.ReactElement | null;
} => {
    const [selectedTabId, setSelectedTabId] = useState(null);
    const { showCrossCampaignFilterInDesktop } = useCrossCampaignEntry();

    const hideTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    const showMenu = useCallback((tabId: CategoryTabIds) => {
        if (hideTimeout.current) {
            clearTimeout(hideTimeout.current);
        }

        if (
            [
                CategoryTabIds.MEN,
                CategoryTabIds.WOMEN,
                CategoryTabIds.KIDS,
                CategoryTabIds.HOME,
            ].includes(tabId)
        ) {
            setSelectedTabId(tabId);
        } else {
            setSelectedTabId(null);
        }
    }, []);

    const hideMenu = useCallback((delay: number = 250) => {
        hideTimeout.current = setTimeout(() => {
            setSelectedTabId(null);
        }, delay);
    }, []);

    const isMenuShown = useCallback(
        (tabId: CategoryTabIds) => {
            return selectedTabId === tabId;
        },
        [selectedTabId],
    );

    const isExpandableTab = useCallback((tabId: CategoryTabIds) => {
        return [
            CategoryTabIds.MEN,
            CategoryTabIds.WOMEN,
            CategoryTabIds.KIDS,
            CategoryTabIds.HOME,
        ].includes(tabId);
    }, []);

    useEffect(() => {
        return () => {
            if (hideTimeout.current) {
                clearTimeout(hideTimeout.current);
            }
        };
    }, []);

    if (!showCrossCampaignFilterInDesktop) {
        return {
            showMenu: () => {},
            hideMenu: () => {},
            isMenuShown: () => false,
            isExpandableTab: () => false,
            CrossCampaignEntryLargeScreen: () => null,
        };
    }

    return {
        showMenu,
        hideMenu,
        isMenuShown,
        isExpandableTab,
        CrossCampaignEntryLargeScreen: () =>
            selectedTabId ? (
                <CrossCampaignEntryLargeScreen
                    showMenu={showMenu}
                    hideMenu={hideMenu}
                    selectedTabId={selectedTabId}
                />
            ) : null,
    };
};
